import type { Constructor, MergeValues } from "./util";

type PartialRecord<K extends string, V> = Partial<Record<K, V>>;

// serializedEvent
//    |> GeneralDeserializerFunction
//    |> DeserializerRegistry[e]
//    |> event
//    |> HandlerFunction[e]

/** Generic event deserializer - converts serialized data to intermediate form */
export type GeneralEventDeserializerFunction<T extends TypedEventEmitter<any, any>> = (
  this: T,
  event: T[" serializedEventId"],
  detail: MergeValues<T[" serializedEvents"]>,
) => unknown;

/** Per-event deserializer - receives (deserialized, serialized), returns final Events[K] */
export type EventDeserializerFunction<
  T extends TypedEventEmitter<any, any>,
  K extends T[" serializedEventId"],
> = (
  this: T,
  deserialized: T[" events"][K],
  serialized: T[" serializedEvents"][K],
) => T[" events"][K] | null;

export type EventDeserializerRegistry<T extends TypedEventEmitter<any, any>> = {
  [K in T[" serializedEventId"]]?: EventDeserializerFunction<T, K>;
};

export type EventHandlerFunction<T extends TypedEventEmitter<any, any>, K extends T[" eventId"]> = (
  this: T,
  detail: T[" events"][K],
) => void;

export type Namespaced<E, N extends string> = {
  [K in keyof E & string as `${N}:${K}`]: E[K];
};

// ============================================================================
// System base: explicit event declaration via generic + name list.
// Methods listed in eventNames become bus-wrapped; others stay regular.
// ============================================================================

export type EventHandlers<E extends Record<string, unknown>> = {
  [K in keyof E]: (detail: E[K]) => void;
};

export interface SystemCtor<G, E extends Record<string, unknown>> {
  new (game: G): { game: G };
  eventNames: readonly (keyof E & string)[];
  /** Phantom for downstream `(typeof Cls)["_events"]` extraction */
  _events?: E;
}

export function System<G>() {
  return function <E extends Record<string, unknown>>(
    eventNames: readonly (keyof E & string)[],
  ): SystemCtor<G, E> {
    class S {
      static eventNames = eventNames;
      declare game: G;
      constructor(game: G) {
        Object.defineProperty(this, "game", {
          value: game,
          enumerable: false,
          configurable: true,
        });
      }
    }
    return S as unknown as SystemCtor<G, E>;
  };
}

export type EventHandlersRegistry<T extends TypedEventEmitter<any, any>> = {
  [K in T[" eventId"]]?: EventHandlerFunction<T, K>;
};

export interface ScheduledEvent<
  T extends TypedEventEmitter<any, any>,
  K extends T[" serializedEventId"],
> {
  event: K;
  atTick: number;
  detail: T[" serializedEvents"][K];
}

export class TypedEventEmitter<
  Events extends Record<keyof SerializedEvents, unknown>,
  SerializedEvents extends Record<string, unknown>,
> extends EventTarget {
  declare " events": Events;
  declare " serializedEvents": SerializedEvents;
  declare " eventId": keyof Events & string;
  declare " serializedEventId": keyof SerializedEvents & string;
  declare " anyEvent": Events[keyof Events & string];
  declare " anySerializedEvent": SerializedEvents[keyof SerializedEvents & string];
  declare " anySheduledEvent": ScheduledEvent<this, this[" serializedEventId"]>;

  get generalEventDeserializer(): GeneralEventDeserializerFunction<this> | null {
    return (this.constructor as any as this).generalEventDeserializer ?? null;
  }
  static get generalEventDeserializer(): GeneralEventDeserializerFunction<any> | null {
    return (this as any)._generalEventDeserializer ?? null;
  }

  get eventDeserializerRegistry(): EventDeserializerRegistry<this> {
    return (this.constructor as any as this).eventDeserializerRegistry;
  }
  static get eventDeserializerRegistry(): Record<string, any> {
    if (!Object.hasOwn(this, "_deserializerRegistry")) {
      (this as any)._deserializerRegistry = {};
    }
    return (this as any)._deserializerRegistry;
  }

  get eventHandlerRegistry(): EventHandlersRegistry<this> {
    return (this.constructor as any as this).eventHandlerRegistry;
  }
  static get eventHandlerRegistry(): EventHandlersRegistry<any> {
    if (!Object.hasOwn(this, "_handlerRegistry")) {
      (this as any)._handlerRegistry = {};
    }
    return (this as any)._handlerRegistry;
  }

  static registerGeneralEventDeserializer<T extends TypedEventEmitter<any, any>>(
    this: Constructor<T>,
    deserializer: GeneralEventDeserializerFunction<T>,
  ): void {
    const registry = this as any as {
      _generalEventDeserializer: GeneralEventDeserializerFunction<T>;
    };
    if (Object.hasOwn(registry, "_generalEventDeserializer")) {
      throw new Error("General event deserializer already registered");
    }
    registry._generalEventDeserializer = deserializer;
  }

  static registerEventDeserializers<T extends TypedEventEmitter<any, any>>(
    this: Constructor<T>,
    deserializersMap: EventDeserializerRegistry<T>,
  ): void {
    const registry = (this as any as T).eventDeserializerRegistry;
    for (const [event, deserializer] of Object.entries(deserializersMap) as [
      T[" serializedEventId"],
      EventDeserializerFunction<T, T[" serializedEventId"]>,
    ][]) {
      if (!deserializer) continue;
      if (Object.hasOwn(registry, event)) {
        throw new Error(`Deserializer for event ${event} already registered`);
      }
      registry[event] = deserializer;
    }
  }

  static registerEventHandlers<T extends TypedEventEmitter<any, any>>(
    this: Constructor<T>,
    handlersMap: EventHandlersRegistry<T>,
  ): void {
    const registry = (this as any as T).eventHandlerRegistry;
    for (const [event, handler] of Object.entries(handlersMap) as [
      T[" eventId"],
      EventHandlerFunction<T, T[" eventId"]>,
    ][]) {
      if (!handler) continue;
      if (Object.hasOwn(registry, event)) {
        throw new Error(`Handler for event ${event} already registered`);
      }
      registry[event] = handler;
    }
  }

  registerEventHandlers() {
    for (const [event, handler] of Object.entries(this.eventHandlerRegistry) as [
      this[" eventId"],
      EventHandlerFunction<this, this[" eventId"]>,
    ][]) {
      if (!handler) continue;
      this.on(event, handler);
    }
  }

  emit<K extends this[" eventId"]>(type: K, detail: this[" events"][K]) {
    this.dispatchEvent(new CustomEvent(type as string, { detail }));
  }

  /**
   * Install a system instance at `this.<ns>`. Each method on the system's
   * prototype is wrapped so calling `this.<ns>.<method>(detail)` emits
   * `<ns>:<kebab-method>` through the bus, and the bus listener invokes the
   * original method body. The 2nd `run?: true` arg is internal — only the
   * bus listener passes it. State fields pass through unchanged.
   */
  installSystem<S extends object>(ns: string, system: S): S {
    const originalProto = Object.getPrototypeOf(system);
    const shimProto = Object.create(originalProto);
    const emitter = this;

    const cls = (system as any).constructor;
    const eventNames: string[] = cls?.eventNames ?? [];

    for (const name of eventNames) {
      const original = (originalProto as any)[name];
      if (typeof original !== "function") {
        throw new Error(
          `System '${ns}' declared event '${name}' but has no method on the prototype`,
        );
      }

      const eventName = `${ns}:${name}`;

      shimProto[name] = function (this: any, detail: unknown, run?: true) {
        if (run) return original.call(this, detail);
        (emitter as TypedEventEmitter<any, any>).emit(eventName, detail);
      };

      emitter.on(eventName as any, (detail: unknown) =>
        (system as any)[name](detail, true),
      );
    }

    Object.setPrototypeOf(system, shimProto);
    Object.defineProperty(this, ns, {
      value: system,
      writable: false,
      enumerable: false,
      configurable: true,
    });
    return system;
  }

  emitSerialized<K extends this[" serializedEventId"]>(
    type: K,
    detail: this[" serializedEvents"][K],
  ) {
    const deserialized = this.deserializeEvent(type, detail);
    if (!deserialized) {
      throw new Error(`Failed to deserialize event: ${type}`);
    }
    this.emit(type, deserialized);
  }

  on<K extends this[" eventId"]>(
    type: K,
    handler: (detail: this[" events"][K]) => void,
  ): () => void {
    const listener = (e: Event) => handler.call(this, (e as CustomEvent).detail);
    this.addEventListener(type as string, listener);
    return () => this.removeEventListener(type as string, listener);
  }

  addLogging(): void {
    const allEvents = new Set([
      ...Object.keys(this.eventHandlerRegistry),
      ...Object.keys(this.eventDeserializerRegistry),
    ]);
    for (const eventName of allEvents) {
      this.on(eventName, (detail) => {
        console.log(`[${String(eventName)}]:`, JSON.stringify(detail));
      });
    }
  }

  get scheduledEvents(): this[" anySheduledEvent"][] {
    throw new Error("scheduledEvents must be implemented by subclass");
  }

  schedule<K extends this[" serializedEventId"]>(
    event: K,
    atTick: number,
    detail: this[" serializedEvents"][K],
  ): void {
    let index = this.scheduledEvents.findIndex((e) => e.atTick > atTick);
    if (index === -1) {
      this.scheduledEvents.push({ event, atTick, detail });
    } else {
      this.scheduledEvents.splice(index, 0, { event, atTick, detail });
    }
  }

  executeScheduledEvents(
    currentTick: number,
  ): (this[" anySheduledEvent"] & Record<"processed", this[" anyEvent"] | null>)[] {
    const events: ReturnType<typeof this.executeScheduledEvents> = [];
    while (this.scheduledEvents[0] && this.scheduledEvents[0].atTick <= currentTick) {
      const scheduledEvent = this.scheduledEvents.shift()!;
      const processed = this.deserializeEvent(scheduledEvent.event, scheduledEvent.detail);
      if (processed !== null) {
        events.push({ ...scheduledEvent, processed });
        this.emit(scheduledEvent.event, processed);
      }
    }
    return events;
  }

  deserializeEvent<K extends this[" serializedEventId"]>(
    event: K,
    detail: this[" serializedEvents"][K],
  ): this[" events"][K] | null {
    const generalDeserializer = this.generalEventDeserializer ?? ((_, e) => e);
    const eventDeserializer = this.eventDeserializerRegistry[event] ?? ((e) => e);

    return eventDeserializer.call(
      this,
      generalDeserializer.call(this, event, detail as any) as any,
      detail,
    );
  }
}
